import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

// Universal renderer for all 200 launch pages. Frontend routes catch any of:
//   /gifts/:occ/:rel, /gift-ideas/:mod, /what-to-get/:slug, /shop/:hub, /about-*, /the-perfect-*, etc.
// then all funnel here and this component fetches its content from /api/programmatic/page?path=<path>.
export default function ProgrammaticPage() {
  const loc = useLocation();
  const [state, setState] = useState({ loading: true, data: null, err: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, err: null });
    api.get(`/programmatic/page?path=${encodeURIComponent(loc.pathname)}`)
      .then((r) => { if (!cancelled) setState({ loading: false, data: r.data, err: null }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, data: null, err: e?.response?.status === 404 ? "not-found" : "load-failed" }); });
    return () => { cancelled = true; };
  }, [loc.pathname]);

  if (state.loading) {
    return <div className="max-w-3xl mx-auto px-6 py-24 text-white/50 text-sm" data-testid="programmatic-loading">Loading…</div>;
  }
  if (state.err === "not-found" || !state.data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-white/70 text-sm" data-testid="programmatic-notfound">
        <h1 className="font-display text-4xl text-white mb-4">Not here.</h1>
        <p>That page doesn't exist. Try <Link to="/shop" className="text-[#FF3D8B] hover:underline">the shop</Link> or <Link to="/blog" className="text-[#FF3D8B] hover:underline">the blog</Link>.</p>
      </div>
    );
  }

  const { title, meta_description, canonical, h1, body_markdown, faq_jsonld, related, category } = state.data;

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-10 py-16" data-testid="programmatic-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={meta_description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={meta_description} />
        <meta property="og:url" content={canonical} />
        {faq_jsonld && <script type="application/ld+json">{JSON.stringify(faq_jsonld)}</script>}
      </Helmet>

      <div className="mb-6"><Link to="/" data-testid="prog-home-link" className="text-white/50 hover:text-white text-xs uppercase tracking-widest">← Home</Link></div>
      <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-3 flex items-center gap-2">
        <Sparkles size={14} /> {category.replace("-", " ")}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-8" data-testid="prog-h1">{h1}</h1>

      <article className="prose prose-invert prose-lg max-w-none prose-headings:font-display prose-headings:text-white prose-p:text-white/80 prose-strong:text-white prose-a:text-[#FF3D8B] hover:prose-a:text-[#ff5ba0] prose-a:no-underline hover:prose-a:underline prose-li:text-white/80" data-testid="prog-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{body_markdown}</ReactMarkdown>
      </article>

      {/* CTA card */}
      <div className="mt-12 rounded-2xl border border-white/10 bg-[#0f0f0f] p-8 text-center" data-testid="prog-cta">
        <h3 className="font-display text-2xl text-white mb-3">Ready to make someone smile?</h3>
        <p className="text-white/60 mb-6 max-w-md mx-auto text-sm">Six scents. Hand-poured in the USA. Under $10 each, ships free over $10.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0]">
            Shop scents <ArrowRight size={14} />
          </Link>
          <Link to="/scent-quiz" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white/80 hover:text-white hover:border-[#A855F7] text-xs uppercase tracking-widest">
            Take the quiz
          </Link>
        </div>
      </div>

      {/* Related internal-link cluster */}
      {related && related.length > 0 && (
        <div className="mt-12" data-testid="prog-related">
          <div className="text-white/60 text-xs uppercase tracking-widest mb-4">You might also like</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {related.map((r, i) => (
              <Link key={r.path} to={r.path} data-testid={`prog-related-${i}`}
                className="px-5 py-4 rounded-xl border border-white/10 bg-white/[.02] hover:border-[#FF3D8B]/40 hover:bg-[#FF3D8B]/5 transition-all text-white/80 hover:text-white text-sm flex items-center justify-between group">
                <span>{r.title}</span>
                <ArrowRight size={14} className="text-white/30 group-hover:text-[#FF3D8B]" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
